// Rank badges in the turret during the warm-up (Design §11.3): by rating from 700 in steps of
// 50 – one to three chevrons in bronze, silver, gold, then gold and platinum stars, then
// crowns – and one each for guests and bots. Kept upright and on a dark disc, so they read at
// turret size.

/** `Member::rank` (protocol): guests, bots, then the steps of rating. */
const RANK_GUEST = 1;
const RANK_BOT = 2;
const RANK_FIRST = 3;

const METALS: [string, string][] = [
  ['#d08a4a', '#6b3a12'], // bronze
  ['#e8edf0', '#6d7880'], // silver
  ['#ffd34d', '#8a6a00'], // gold
  ['#a8ecff', '#22617c'], // platinum
];

/** Badge `rank` as a little picture of `size` CSS px (lists on the start page); none: undefined. */
export function badgeIcon(rank: number, size = 16): HTMLCanvasElement | undefined {
  if (rank < RANK_GUEST) return undefined;
  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = canvas.height = Math.round(size * dpr);
  canvas.style.width = canvas.style.height = `${size}px`;
  canvas.className = 'badge';
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(dpr, 0, 0, dpr, size / 2 * dpr, size / 2 * dpr);
  drawBadge(ctx, size * 0.45, rank, 0);
  return canvas;
}

/** Draws badge `rank` around the origin within ± `s`, upright although the tank turns by `angle`. */
export function drawBadge(ctx: CanvasRenderingContext2D, s: number, rank: number, angle: number): void {
  if (rank < RANK_GUEST) return;
  ctx.save();
  ctx.rotate(-angle);
  ctx.beginPath();
  ctx.arc(0, 0, s * 1.05, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(15, 18, 12, 0.7)';
  ctx.fill();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (rank === RANK_GUEST) guest(ctx, s);
  else if (rank === RANK_BOT) gear(ctx, s);
  else {
    const step = rank - RANK_FIRST;
    const [tier, n] = [Math.floor(step / 3), (step % 3) + 1];
    if (tier < 3) chevrons(ctx, s, n, METALS[tier]);
    else if (tier < 5) stars(ctx, s, n, METALS[tier - 1]);
    else crown(ctx, s, n);
  }
  ctx.restore();
}

/** Fills the current path in `metal` with its dark edge. */
function metal(ctx: CanvasRenderingContext2D, [fill, edge]: [string, string], s: number): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = edge;
  ctx.lineWidth = s * 0.1;
  ctx.stroke();
}

/** One to three chevrons, stacked. */
function chevrons(ctx: CanvasRenderingContext2D, s: number, n: number, [fill, edge]: [string, string]): void {
  const gap = s * 0.42;
  for (let k = 0; k < n; k++) {
    const y = (k - (n - 1) / 2) * gap - s * 0.12;
    ctx.beginPath();
    ctx.moveTo(-s * 0.62, y - s * 0.12);
    ctx.lineTo(0, y + s * 0.3);
    ctx.lineTo(s * 0.62, y - s * 0.12);
    ctx.strokeStyle = edge;
    ctx.lineWidth = s * 0.34;
    ctx.stroke();
    ctx.strokeStyle = fill;
    ctx.lineWidth = s * 0.2;
    ctx.stroke();
  }
}

function starPath(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  for (let k = 0; k < 10; k++) {
    const a = -Math.PI / 2 + (k * Math.PI) / 5;
    const d = k % 2 ? r * 0.45 : r;
    ctx.lineTo(x + Math.cos(a) * d, y + Math.sin(a) * d);
  }
  ctx.closePath();
}

/** One star big, two side by side, three in a triangle. */
function stars(ctx: CanvasRenderingContext2D, s: number, n: number, m: [string, string]): void {
  const spots: [number, number, number][] =
    n === 1 ? [[0, 0, 0.8]] : n === 2 ? [[-0.42, 0, 0.5], [0.42, 0, 0.5]] : [[0, -0.38, 0.45], [-0.42, 0.32, 0.45], [0.42, 0.32, 0.45]];
  for (const [x, y, r] of spots) {
    starPath(ctx, x * s, y * s, r * s);
    metal(ctx, m, s);
  }
}

/** A crown: gold, platinum, platinum with a red gem. */
function crown(ctx: CanvasRenderingContext2D, s: number, n: number): void {
  ctx.beginPath();
  const pts = [[-0.65, 0.45], [-0.65, -0.35], [-0.32, 0.02], [0, -0.55], [0.32, 0.02], [0.65, -0.35], [0.65, 0.45]];
  for (const [x, y] of pts) ctx.lineTo(x * s, y * s);
  ctx.closePath();
  metal(ctx, n === 1 ? METALS[2] : METALS[3], s);
  if (n === 3) {
    ctx.beginPath();
    ctx.arc(0, s * 0.18, s * 0.17, 0, Math.PI * 2);
    ctx.fillStyle = '#e8263a';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(-s * 0.05, s * 0.13, s * 0.05, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
  }
}

/** A guest: a grey question mark. */
function guest(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.fillStyle = '#c9ccc4';
  ctx.font = `bold ${s * 1.3}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('?', 0, s * 0.06);
}

/** A bot: a steel gear. */
function gear(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath();
  for (let k = 0; k < 32; k++) {
    const a = (k * Math.PI) / 16; // 8 teeth
    const d = (k % 4 < 2 ? 0.78 : 0.56) * s;
    ctx.lineTo(Math.cos(a) * d, Math.sin(a) * d);
  }
  ctx.closePath();
  ctx.moveTo(s * 0.22, 0);
  ctx.arc(0, 0, s * 0.22, 0, Math.PI * 2, true);
  ctx.fillStyle = '#9aa7b1';
  ctx.fill('evenodd');
  ctx.strokeStyle = '#3d4850';
  ctx.lineWidth = s * 0.08;
  ctx.stroke();
}
